// src/oura/hooks/useMySetup.ts
//
// The client's own set-up progress, for the figures that count it (#4026
// C-FE-6): how many access steps are theirs to do, and how many are held.
//
// Its own read, because `GET /businesses/mine/onboarding` is a different route
// from the client page, and the two numbers have to come from the same place
// wherever they are shown -- Home, Decisions, and the Decisions badge -- or a
// client sees 6 on one screen and 4 on another.

import { useEffect, useState } from "react";
import { getMyOnboarding, type MerchantFirstCheck, type MerchantForm, type MerchantOnboarding } from "@service/aeo/onboarding";
import { merchantProgress } from "../onboarding/derive";
import { isTheirsDone, merchantSteps, ONE_SETUP_UI, type MerchantStepKey } from "../onboarding/oneSetup";

export interface MySetup {
    /** Access steps held: done, over the whole term. Under one setup, the Setup page's steps whose part is done. */
    done: number;
    /** Steps that are THE CLIENT'S to do. A step waiting on staff is not one. Under one setup, the page's steps. */
    mine: number;
    /** The onboarding questions, when the backend says (#4133). Counted in `mine` only under one setup. */
    form?: MerchantForm | null;
    /** The First AI check's standing, when the backend sends it. Absent: not known, so no first-run screens. */
    firstCheck?: MerchantFirstCheck | null;
    /** Under one setup: the Setup page's steps still theirs, in page order, for the banner that names them. */
    left?: Array<{ key: MerchantStepKey; title: string; sentTo: string | null }>;
}

/**
 * How the set-up read ended. Five states, because three of them mean
 * different things to a count and collapsing them to `null` made a failed
 * read look like finished work:
 *
 *   loading     -- not back yet
 *   ready       -- counted
 *   none        -- 404: this client has no set-up record, so no set-up steps.
 *                  AN ANSWER, not an absence, and it counts as zero.
 *   unavailable -- the read failed. Nothing is known about the steps.
 *   off         -- not asked for (not a merchant). Contributes nothing.
 */
export type MySetupRead =
    | { status: "loading" }
    | { status: "ready"; setup: MySetup }
    | { status: "none" }
    | { status: "unavailable" }
    | { status: "off" };

/**
 * What the set-up read adds to a figure, and whether the figure is then the
 * whole story.
 *
 * `known: false` is the house rule at work: a count that is missing its
 * set-up half must not be printed as exact. The caller says "at least N"
 * instead of quietly understating it (#4026 C-FE-6).
 */
export type SetupPart = { known: true; done: number; mine: number } | { known: false };

export function setupPart(read: MySetupRead): SetupPart {
    switch (read.status) {
        case "ready":
            return { known: true, ...read.setup };
        // No record and not asked are both real zeros, not gaps.
        case "none":
        case "off":
            return { known: true, done: 0, mine: 0 };
        default:
            return { known: false };
    }
}

/** How often an open page reads set-up again while the first AI check moves on by itself. */
export const FIRST_CHECK_POLL_MS = 30_000;

export interface FollowedSetup {
    stop(): void;
    /** A newer read from elsewhere (the merchant's own change): drops any read in flight and follows on from it. */
    saw(m: MerchantOnboarding | null): void;
}

/**
 * Reads the merchant's set-up, then reads it again every FIRST_CHECK_POLL_MS,
 * and when the tab comes back, for as long as the first AI check is known and
 * not ready. The check moves from preparing to running to reviewing to ready
 * on the server, with nothing for the merchant to press, so an open page has
 * to look again to follow it. A late answer never overwrites a newer one.
 */
export function followMySetup(onRead: (m: MerchantOnboarding | null) => void, onFail: () => void): FollowedSetup {
    let stopped = false;
    let timer: number | undefined;
    let sequence = 0;
    let moving = false;
    const follow = (m: MerchantOnboarding | null) => {
        moving = Boolean(m?.firstCheck) && m?.firstCheck?.status !== "ready";
        if (moving) timer = window.setTimeout(read, FIRST_CHECK_POLL_MS);
    };
    function read() {
        const request = ++sequence;
        window.clearTimeout(timer);
        getMyOnboarding().then(
            (m) => {
                if (stopped || request !== sequence) return;
                onRead(m);
                follow(m);
            },
            () => {
                if (stopped || request !== sequence) return;
                onFail();
                if (moving) timer = window.setTimeout(read, FIRST_CHECK_POLL_MS);
            },
        );
    }
    const onVisible = () => {
        if (moving && document.visibilityState === "visible") read();
    };
    document.addEventListener("visibilitychange", onVisible);
    read();
    return {
        stop() {
            stopped = true;
            sequence += 1;
            window.clearTimeout(timer);
            document.removeEventListener("visibilitychange", onVisible);
        },
        saw(m) {
            if (stopped) return;
            sequence += 1;
            window.clearTimeout(timer);
            follow(m);
        },
    };
}

/**
 * The last answer this tab had, so a page that mounts again (Home ↔ Visibility)
 * starts from it instead of "loading" and never draws what first run hides
 * for a moment (#4440). A fresh read still runs and replaces it.
 */
let lastKnown: MySetupRead | null = null;

export function useMySetup(enabled = true): MySetupRead {
    const [read, setRead] = useState<MySetupRead>(enabled ? lastKnown ?? { status: "loading" } : { status: "off" });

    useEffect(() => {
        if (!enabled) {
            setRead({ status: "off" });
            return;
        }
        let counted = false;
        setRead(lastKnown ?? { status: "loading" });
        const followed = followMySetup(
            (m) => {
                counted = true;
                // `null` here is the service's 404: no record of set-up.
                if (!m) { setRead(lastKnown = { status: "none" }); return; }
                // One setup counts what the Setup page shows: its steps, the
                // questions among them, so the badge and the page agree.
                if (ONE_SETUP_UI) {
                    const steps = merchantSteps(m);
                    const done = steps.filter(isTheirsDone).length;
                    const left = steps.filter((s) => !isTheirsDone(s)).map((s) => ({ key: s.key, title: s.title, sentTo: s.sentTo }));
                    setRead(lastKnown = { status: "ready", setup: { done, mine: steps.length - done, form: m.form ?? null, firstCheck: m.firstCheck ?? null, left } });
                    return;
                }
                const { done, mine } = merchantProgress(m);
                setRead(lastKnown = { status: "ready", setup: { done, mine, form: m.form ?? null, firstCheck: m.firstCheck ?? null } });
            },
            // A count is not worth taking the screen down for -- but it is
            // also not worth lying about, so the state is kept. A later read
            // that fails keeps the last count rather than lose it.
            () => { if (!counted) setRead({ status: "unavailable" }); },
        );
        return () => followed.stop();
    }, [enabled]);

    return read;
}
